<script>
export const MEDICATIONS_SCHEMA = {
    widgetType: 'medicationList',
    label: 'Medications',
    fields: [
        { name: 'name', type: 'string', required: true, description: 'Medication name (e.g. Lisinopril 10mg)' },
        { name: 'dosage', type: 'string', required: false, description: 'Dosage instructions' },
        { name: 'frequency', type: 'string', required: false, description: 'Frequency (e.g. 1 tablet daily)' },
        {
            name: 'effectiveness',
            type: 'choice',
            required: false,
            description: 'How effective is the medication',
            options: [
                { value: 'very', label: 'Very effective' },
                { value: 'somewhat', label: 'Somewhat effective' },
                { value: 'not', label: 'Not effective' },
            ],
        },
        {
            name: 'takenAsDirected',
            type: 'choice',
            required: false,
            description: 'Is the medication taken as directed',
            options: [
                { value: 'always', label: 'Always' },
                { value: 'usually', label: 'Usually' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'never', label: 'Never' },
            ],
        },
    ],
};
</script>

<script setup>
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getClinicalDrugByName } from '@/common/api/rxnorm.api';
import * as permissions from '@/common/permissionConstants';
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnDialog from '@/components/BnDialog.vue';
import BnListBox from '@/components/BnListBox.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn';
import DataProvider from '@/components/DataProvider';
import { Coding, MedicationRequest } from '@/fhirworks';
import { useScriptSureApi } from '@/composables/api/scriptSure/useScriptSureApi';
import { useScriptSureClinicalSync } from '@/composables/api/scriptSure/useScriptSureClinicalSync';
import { formatDate } from '@/filters/dateTimeFilters';
import { useAuthStore } from '@/stores/auth';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { debounce } from 'lodash';
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useDisplay } from 'vuetify';
import { useIntervalFn } from '@vueuse/core';
import { validateChoiceFields } from '@/components/questionnaires/widgets/widgetValidation';

const vuetifyDisplay = useDisplay();

const app = getCurrentInstance();
const isPortal = app.appContext.config.globalProperties?.isPortal;

const effectivenessOptions = computed(() => [
    new Coding({
        code: 'very',
        system: 'http://code.system/is-medication-effective',
        display: 'Very effective',
    }),
    new Coding({
        code: 'somewhat',
        system: 'http://code.system/is-medication-effective',
        display: 'Somewhat effective',
    }),
    new Coding({
        code: 'not',
        system: 'http://code.system/is-medication-effective',
        display: 'Not effective',
    }),
]);

const takenAsDirectedOptions = computed(() => [
    new Coding({
        code: 'always',
        system: '',
        display: 'Always',
    }),
    new Coding({
        code: 'usually',
        system: '',
        display: 'Usually',
    }),
    new Coding({
        code: 'sometimes',
        system: '',
        display: 'Sometimes',
    }),
    new Coding({
        code: 'rarely',
        system: '',
        display: 'Rarely',
    }),
    new Coding({
        code: 'never',
        system: '',
        display: 'Never',
    }),
]);

const props = defineProps({
    title: String,
    printView: Boolean,
    isLocked: Boolean,
    modelValue: [Array, Object, String],
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'compact'].includes(value),
    },
    showInactive: {
        type: Boolean,
        default: false,
    },
    scriptSurePatientId: {
        type: String,
        default: null,
    },
    patient: {
        type: Object,
        default: null,
    },
    treatmentPlanMedsOnly: {
        type: Boolean,
        default: false,
    },
    autoRefresh: {
        type: Boolean,
        default: false,
    },
});

const patientId = computed(() => props.patient?.id);

// Fall back to the patient resource so the widget works in form contexts where the prop isn't wired up
const effectiveScriptSurePatientId = computed(() => props.scriptSurePatientId || props.patient?.scriptSurePatientId || null);

const isCompact = computed(() => props.variant === 'compact');

const readOnly = computed(() => props.isLocked || props.printView);

const canViewMedications = computed(() => authStore.hasPermission([permissions.VIEW_MED_LIST, permissions.UPDATE_MED_LIST]) || isPortal);
const viewOnlyMedications = computed(() => !authStore.hasPermission(permissions.UPDATE_MED_LIST) && !isPortal && !readOnly.value);

const emit = defineEmits(['update:modelValue']);
const authStore = useAuthStore();
const bnDialogRef = ref(null);
const medicationsDP = ref(null);
const medicationRequestData = ref([]);

const {
    pause: pauseRefreshTimer,
    resume: resumeRefreshTimer,
    isActive: refreshTimerActive,
} = useIntervalFn(
    () => {
        medicationsDP.value?.silentRefresh();
    },
    30000,
    { immediate: false },
);

watch(
    () => props.autoRefresh,
    (enabled) => {
        if (enabled && !refreshTimerActive.value) {
            resumeRefreshTimer();
        } else if (!enabled && refreshTimerActive.value) {
            pauseRefreshTimer();
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    pauseRefreshTimer();
});

// Edit dialog state
const editDialog = ref(false);
const editingMedication = ref(null);
const originalMedication = ref(null); // Reference to original medication being edited
const isNewMedication = ref(false);

const medicationForm = computed(() => ({
    medicationName: editingMedication.value?.medication?.CodeableConcept?.text || '',
}));
const medicationValidations = {
    medicationForm: {
        medicationName: { required },
    },
};
const v$ = useVuelidate(medicationValidations, { medicationForm });

const medicationQuery = computed(() => {
    if (props.isLocked && props.modelValue) {
        return { resource: props.modelValue };
    }

    return {
        query: [
            { query: `subject=${patientId.value}`, resourceType: 'MedicationRequest' },
            { query: `subject=${patientId.value}&code=medicationList`, resourceType: 'List' },
        ],
    };
});

const medications = computed(() => {
    return medicationRequestData.value.filter((resource) => resource.resourceType === 'MedicationRequest');
});

const isMedicationInactive = (med) => {
    if (['stopped', 'cancelled', 'completed', 'entered-in-error'].includes(med.status)) return true;
    if (!med.effective?.end) return false;
    const endDate = new Date(med.effective.end);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today;
};

const displayedMedications = computed(() => {
    let meds = medications.value.filter((med) => med.medication?.CodeableConcept?.text);

    if (!isCompact.value || !props.showInactive) {
        meds = meds.filter((med) => !isMedicationInactive(med));
    }

    if (props.treatmentPlanMedsOnly) {
        meds = meds.filter((med) => med.includeInTreatmentPlan === true);
    }

    // Active first, then inactive — each group alphabetical by medication name (case-insensitive).
    const nameOf = (med) => (med.medication?.CodeableConcept?.text || '').toLowerCase();
    return [...meds].sort((a, b) => {
        const aInactive = isMedicationInactive(a) ? 1 : 0;
        const bInactive = isMedicationInactive(b) ? 1 : 0;
        if (aInactive !== bInactive) return aInactive - bInactive;
        return nameOf(a).localeCompare(nameOf(b));
    });
});

// Count of inactive medications (for parent component to know if menu should show)
const inactiveMedicationsCount = computed(() => {
    return medications.value.filter((med) => med.medication?.CodeableConcept?.text && isMedicationInactive(med)).length;
});

watch(
    () => medicationRequestData.value,
    () => {
        if (medicationRequestData.value && !props.isLocked) {
            emit('update:modelValue', medicationRequestData.value);
        }
    },
    { immediate: true, deep: true },
);

// Open dialog to add a new medication
const openAddDialog = () => {
    medList.value = [];
    searchRxNorm.value = null;
    medNameBlurred.value = false;

    const newMedication = new MedicationRequest({
        subject: { id: patientId.value, resourceType: 'Patient' },
        status: 'active',
        medication: {
            CodeableConcept: {
                text: '',
            },
        },
        intent: 'proposal',
        dateAsserted: new Date().toISOString(),
        reported: { reference: { id: patientId.value, resourceType: 'Patient' } },
        includeInTreatmentPlan: true,
    });

    if (isPortal) {
        newMedication.status = 'draft';
    }

    editingMedication.value = newMedication;
    isNewMedication.value = true;
    v$.value.medicationForm.$reset();
    editDialog.value = true;
};

// Open dialog to edit an existing medication
const openEditDialog = (medication) => {
    if (viewOnlyMedications.value || readOnly.value) return;

    medList.value = [];
    searchRxNorm.value = null;
    medNameBlurred.value = true;
    // Store reference to original and work with a deep copy
    // Use JSON parse/stringify to properly clone FHIR resource objects
    originalMedication.value = medication;
    const medicationJson = typeof medication.toJSON === 'function' ? medication.toJSON() : JSON.parse(JSON.stringify(medication));
    editingMedication.value = new MedicationRequest(medicationJson);
    isNewMedication.value = false;
    v$.value.medicationForm.$reset();
    editDialog.value = true;
    historyData.value = null;
    if (medication.id) {
        fetchMedicationHistory(medication.id);
    }
};

const saveMedication = async () => {
    v$.value.medicationForm.$touch();
    if (!(await v$.value.medicationForm.$validate())) return;

    editingMedication.value.updatedBy = {
        id: authStore.practitioner.id,
        resourceType: authStore.practitioner.resourceType,
    };

    if (isNewMedication.value) {
        await medicationsDP.value.save(editingMedication.value);
    } else {
        await medicationsDP.value.save(editingMedication.value);
        const index = medications.value.findIndex((m) => m.id === editingMedication.value.id);
        if (index !== -1) {
            medications.value[index] = editingMedication.value;
        }
    }

    editDialog.value = false;
    editingMedication.value = null;
    originalMedication.value = null;
    historyData.value = null;
};

const cancelEdit = () => {
    editDialog.value = false;
    editingMedication.value = null;
    originalMedication.value = null;
    historyData.value = null;
};

// Reconcile mode state
const { syncMedicationToScriptSure, ensureScriptSurePatient, resolveAndSyncResource, hasScriptSureCredentials, consumeLastRegistrationUrl, buildSignInDataForPatient, loadActingOnBehalfPrescriberId } =
    useScriptSureClinicalSync();
const { signIn } = useScriptSureApi();

// Snackbar notification for ScriptSure missing patient data
const scriptSureSnackbar = ref(false);
const scriptSureSnackbarMessage = ref('');
const reconcileMode = ref(false);
const selectedForReconcile = ref(new Set());
const reconciling = ref(false);

const canReconcile = computed(() => {
    return authStore.hasPermission(permissions.UPDATE_MED_LIST) && !readOnly.value && displayedMedications.value.length > 0;
});

// e-Prescribe gating: ScriptSure enabled, prescriber registered, not the patient portal, has medication permission.
// hasScriptSureCredentials already covers account.scriptSureEnabled + practiceId + practitioner.scriptSurePrescriberId.
const canPrescribe = computed(() => {
    return hasScriptSureCredentials.value && !isPortal && authStore.hasPermission(permissions.UPDATE_MED_LIST) && !readOnly.value;
});

const isPrescribing = ref(false);
const isRefreshing = ref(false);

const openPrescribe = async () => {
    if (isPrescribing.value) return;
    isPrescribing.value = true;
    try {
        const result = await ensureScriptSurePatient(props.patient);

        if (result?.missingFields) {
            scriptSureSnackbarMessage.value = `e-Prescribe unavailable: missing ${result.missingFields.join(', ')}`;
            scriptSureSnackbar.value = true;
            return;
        }
        if (!result) {
            scriptSureSnackbarMessage.value = 'e-Prescribe is not configured or credentials are missing';
            scriptSureSnackbar.value = true;
            return;
        }

        // ensureScriptSurePatient may have just registered the patient and cached the session URL;
        // consume it to avoid a redundant signIn round-trip.
        let scriptSureUrl = consumeLastRegistrationUrl();

        if (!scriptSureUrl) {
            const signInData = buildSignInDataForPatient(props.patient);
            signInData.scriptSurePatientId = result;
            const response = await signIn(signInData);
            scriptSureUrl = response?.scriptSureUrl;
        }

        if (scriptSureUrl) {
            const width = 1200;
            const height = 800;
            const left = window.screenX + 50;
            const top = window.screenY + 50;
            window.open(scriptSureUrl, 'ScriptSure', `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`);
        } else {
            scriptSureSnackbarMessage.value = 'Failed to open e-Prescribe';
            scriptSureSnackbar.value = true;
        }
    } catch (err) {
        console.error('Failed to open e-Prescribe:', err);
        scriptSureSnackbarMessage.value = err?.response?.data?.message || err?.message || 'Failed to open e-Prescribe';
        scriptSureSnackbar.value = true;
    } finally {
        isPrescribing.value = false;
    }
};

const refreshMedications = async () => {
    if (isRefreshing.value) return;
    isRefreshing.value = true;
    try {
        await medicationsDP.value?.refresh();
    } finally {
        isRefreshing.value = false;
    }
};

const selectedCount = computed(() => selectedForReconcile.value.size);

const toggleReconcileMode = () => {
    if (reconcileMode.value) {
        // Exiting reconcile mode - clear selections
        reconcileMode.value = false;
        selectedForReconcile.value = new Set();
    } else {
        reconcileMode.value = true;
    }
};

const toggleMedicationSelection = (medId) => {
    const newSet = new Set(selectedForReconcile.value);
    if (newSet.has(medId)) {
        newSet.delete(medId);
    } else {
        newSet.add(medId);
    }
    selectedForReconcile.value = newSet;
};

const allSelectedForReconcile = computed(() => {
    const medsWithIds = displayedMedications.value.filter((med) => med.id);
    return medsWithIds.length > 0 && selectedForReconcile.value.size === medsWithIds.length;
});

const toggleAllForReconcile = () => {
    if (allSelectedForReconcile.value) {
        selectedForReconcile.value = new Set();
    } else {
        const newSet = new Set();
        displayedMedications.value.forEach((med) => {
            if (med.id) newSet.add(med.id);
        });
        selectedForReconcile.value = newSet;
    }
};

/**
 * Find the active PROGRAM encounter for the patient.
 */
const findProgramEncounter = async () => {
    if (!patientId.value) return null;
    try {
        const response = await $httpFhirApi.get(`Encounter?subject=${patientId.value}&status=in-progress&appointment:missing=true&_elements=id,type&_count=50`);
        const encounters = Array.isArray(response.data) ? response.data : [];
        const programEncounter = encounters.find((enc) => enc.type?.some((t) => t.text === 'PROGRAM'));
        if (programEncounter) {
            return { id: programEncounter.id, resourceType: 'Encounter' };
        }
        return null;
    } catch (err) {
        console.error('Failed to find program encounter:', err);
        return null;
    }
};

/**
 * Get the last reconciliation entry from a medication's reconciliation array.
 */
const getLastReconciliation = (med) => {
    if (!med.reconciliation?.length) return null;
    return med.reconciliation[med.reconciliation.length - 1];
};

/**
 * Get the most recent ScriptSure webhook log entry for a medication.
 */
const getLatestWebhookLog = (med) => {
    if (!med.scriptSureWebhookLog?.length) return null;
    return med.scriptSureWebhookLog[med.scriptSureWebhookLog.length - 1];
};

const reconcileMedications = async () => {
    if (selectedForReconcile.value.size === 0) return;

    reconciling.value = true;
    try {
        const now = new Date().toISOString();
        const practitionerId = authStore.practitioner?.id;

        // Find the active PROGRAM encounter for this patient
        const programEncounterRef = await findProgramEncounter();

        // Resolve scriptSurePatientId: use prop, or auto-initialize from patient prop
        const scriptSurePatientId = props.scriptSurePatientId || (props.patient ? await ensureScriptSurePatient(props.patient) : null);

        // If ensureScriptSurePatient returned missing fields, notify the user once
        if (scriptSurePatientId && typeof scriptSurePatientId === 'object' && scriptSurePatientId.missingFields) {
            scriptSureSnackbarMessage.value = `e-Prescribe sync skipped: missing ${scriptSurePatientId.missingFields.join(', ')}`;
            scriptSureSnackbar.value = true;
        }

        // Only sync if we got a valid string ID
        const canSync = typeof scriptSurePatientId === 'string' && scriptSurePatientId;

        // Update each selected medication with reconciliation data
        for (const med of displayedMedications.value) {
            if (!selectedForReconcile.value.has(med.id)) continue;

            // Build the reconciliation entry
            const reconciliationEntry = { date: now };
            if (practitionerId) {
                reconciliationEntry.practitioner = { id: practitionerId, resourceType: 'Practitioner' };
            }
            if (programEncounterRef) {
                reconciliationEntry.encounter = programEncounterRef;
            }

            // Push to the reconciliation array
            if (!med.reconciliation) {
                med.reconciliation = [];
            }
            med.reconciliation.push(reconciliationEntry);

            if (med.status === 'draft') {
                med.status = 'active';
            }

            await medicationsDP.value.save(med);

            // Push to ScriptSure if patient has a connection — skip free-text meds without RxNorm coding
            if (canSync && !isFreeTextMedication(med)) {
                try {
                    const syncResult = await syncMedicationToScriptSure(med, scriptSurePatientId);
                    if (syncResult?.drugNotFound) {
                        // FDB lookup missed (commonly a retired RxNorm code) — prescription still posts with raw codes, so don't alarm the user
                        console.warn('ScriptSure FDB miss for', med.medication?.CodeableConcept?.text);
                    }
                } catch (err) {
                    console.error('Failed to sync medication to ScriptSure:', err);
                }
            }
        }

        // Exit reconcile mode
        reconcileMode.value = false;
        selectedForReconcile.value = new Set();
    } finally {
        reconciling.value = false;
    }
};

// Modifies editingMedication (not originalMedication) so the change respects the dialog's Save/Cancel flow.
const toggleTreatmentPlanFromDialog = () => {
    if (!editingMedication.value) return;
    editingMedication.value.includeInTreatmentPlan = !editingMedication.value.includeInTreatmentPlan;
};

const addItem = async (data) => {
    const invalidChoices = validateChoiceFields(data, MEDICATIONS_SCHEMA);
    if (invalidChoices) return invalidChoices;

    const med = new MedicationRequest({
        subject: { id: patientId.value, resourceType: 'Patient' },
        status: 'active',
        intent: 'proposal',
        medication: { CodeableConcept: { text: data.name || '' } },
        dateAsserted: new Date().toISOString(),
        reported: { reference: { id: patientId.value, resourceType: 'Patient' } },
        includeInTreatmentPlan: true,
    });
    if (data.dosage) med.dosageInstruction = [{ text: data.dosage }];
    if (data.frequency) {
        med.dosageInstruction = med.dosageInstruction || [{}];
        med.dosageInstruction[0].timing = { code: { text: data.frequency } };
    }
    if (data.effectiveness) {
        const match = effectivenessOptions.value.find((o) => o.code === data.effectiveness);
        if (match) med.isMedicationEffective = match;
    }
    if (data.takenAsDirected) {
        const match = takenAsDirectedOptions.value.find((o) => o.code === data.takenAsDirected);
        if (match) med.takenAsDirected = match;
    }
    await medicationsDP.value.save(med);

    const nilKnownList = medicationRequestData.value.find((r) => r.resourceType === 'List' && r.emptyReason?.coding?.[0]?.code === 'nilknown');
    if (nilKnownList) {
        await medicationsDP.value.deleteItem(nilKnownList);
    }

    return { success: true };
};

const getItems = () => {
    return medications.value
        .filter((med) => med.medication?.CodeableConcept?.text)
        .map((med) => ({
            name: med.medication?.CodeableConcept?.text || '',
            dosage: med.dosageInstruction?.[0]?.text || '',
            frequency: med.dosageInstruction?.[0]?.timing?.code?.text || '',
            effectiveness: med.isMedicationEffective?.code || '',
            takenAsDirected: med.takenAsDirected?.code || '',
            status: med.status || '',
        }));
};

defineExpose({
    openAddDialog,
    openEditDialog,
    inactiveMedicationsCount,
    reconcileMode,
    toggleReconcileMode,
    canPrescribe,
    openPrescribe,
    isPrescribing,
    refreshMedications,
    isRefreshing,
    v$,
    addItem,
    getItems,
});

const _registerWidgetApi = inject('registerWidgetApi', null);
const _unregisterWidgetApi = inject('unregisterWidgetApi', null);

onMounted(() => {
    _registerWidgetApi?.('medicationList', { addItem, getItems, schema: MEDICATIONS_SCHEMA });
    // Ensure acting-on-behalf prescriber ID is loaded so canPrescribe is accurate.
    loadActingOnBehalfPrescriberId?.();
});

onBeforeUnmount(() => {
    _unregisterWidgetApi?.('medicationList');
});

const isEditingMedicationInactive = computed(() => {
    return editingMedication.value ? isMedicationInactive(editingMedication.value) : false;
});

const persistStatusChange = async (medicationToUpdate, operationLabel) => {
    medicationToUpdate.updatedBy = {
        id: authStore.practitioner.id,
        resourceType: authStore.practitioner.resourceType,
    };
    await medicationsDP.value.save(medicationToUpdate);

    if (medicationToUpdate.scriptSurePrescriptionId) {
        await resolveAndSyncResource({
            resource: medicationToUpdate,
            patient: props.patient,
            scriptSurePatientId: props.scriptSurePatientId,
            syncFn: syncMedicationToScriptSure,
            operationLabel,
        });
    }

    editDialog.value = false;
    editingMedication.value = null;
    originalMedication.value = null;
    historyData.value = null;
};

// Discontinue dialog state
const discontinueDialog = ref(false);
const discontinueDate = ref(null);
const discontinueReason = ref('');
const discontinueTarget = ref(null);

const inactivateMedication = (medication) => {
    discontinueTarget.value = originalMedication.value || medication;
    discontinueDate.value = new Date().toISOString().split('T')[0];
    discontinueReason.value = '';
    discontinueDialog.value = true;
};

const cancelDiscontinue = () => {
    discontinueDialog.value = false;
    discontinueTarget.value = null;
    discontinueDate.value = null;
    discontinueReason.value = '';
};

const confirmDiscontinue = async () => {
    const medicationToInactivate = discontinueTarget.value;
    if (!medicationToInactivate) return;

    if (!medicationToInactivate.effective) medicationToInactivate.effective = {};
    medicationToInactivate.effective.end = discontinueDate.value;
    medicationToInactivate.status = 'stopped';

    const trimmedReason = (discontinueReason.value || '').trim();
    if (trimmedReason) {
        medicationToInactivate.statusReason = { text: trimmedReason };
    } else {
        medicationToInactivate.statusReason = undefined;
    }

    await persistStatusChange(medicationToInactivate, 'medication inactivation');

    discontinueDialog.value = false;
    discontinueTarget.value = null;
    discontinueDate.value = null;
    discontinueReason.value = '';
};

const markEnteredInError = (medication) => {
    const options = {
        header: 'Entered in error',
        message: 'Are you sure you want to mark this medication as entered in error?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        const medicationToUpdate = originalMedication.value || medication;
        medicationToUpdate.status = 'entered-in-error';
        await persistStatusChange(medicationToUpdate, 'medication entered-in-error');
    });
};

const restoreMedication = (medication) => {
    const options = {
        header: 'Restore',
        message: 'Are you sure you want to restore this medication to active?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        // Remove end date and restore status to active
        const medicationToRestore = originalMedication.value || medication;
        if (medicationToRestore.effective) {
            medicationToRestore.effective = { start: medicationToRestore.effective.start };
        }
        medicationToRestore.status = 'active';
        medicationToRestore.statusReason = undefined;
        await persistStatusChange(medicationToRestore, 'medication reactivation');
    });
};

const updateFrequency = (med, frequency) => {
    if (med.dosageInstruction?.[0]) {
        med.dosageInstruction[0].timing = { code: { text: frequency } };
        return;
    }

    med.dosageInstruction = [{ timing: { code: { text: frequency } } }];
};

const updateEffective = (med, prop, value) => {
    let existingProp = prop === 'start' ? 'end' : 'start';
    if (!med.effective) med.effective = {};

    if (!value) {
        // Allow clearing a date (e.g. removing end date to reactivate)
        med.effective = { [existingProp.toString()]: med.effective[existingProp.toString()] };
        if (prop === 'end') {
            // Clearing the end date hides the Inactive reason field; clear the underlying value too.
            med.statusReason = undefined;
        }
        return;
    }

    if (!med.effective?.[prop.toString()]) {
        med.effective = { [existingProp.toString()]: med.effective[existingProp.toString()], [prop.toString()]: value };
    }

    med.effective[prop.toString()] = value;
};

const medList = ref([]);

const searchRxNorm = ref(null);
const debouncedSearch = debounce(
    function (val) {
        if (!val) return;
        getClinicalDrugByName(val).then((sub) => {
            if (!sub) return;
            medList.value = sub;
        });
    },
    500,
    { maxWait: 750 },
);
watch(() => searchRxNorm.value, debouncedSearch);

const setMedication = function (med, item) {
    if (typeof item === 'string' && item) {
        med.medication.CodeableConcept.text = item;
        med.medication.CodeableConcept.coding = [];
    }

    if (!searchRxNorm.value || !item) return;
    if (item.code) {
        med.medication.CodeableConcept = {
            text: item.display,
            coding: [
                {
                    code: item.code,
                    system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
                    display: item.display,
                },
            ],
        };
    }
};

const isFreeTextMedication = (med) => {
    const coding = med.medication?.CodeableConcept?.coding;
    return !coding || coding.length === 0 || !coding.some((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm' && c.code);
};

const isPrescribedMedication = (med) => med.intent === 'order';

const editingMedIsFreeText = computed(() => {
    return editingMedication.value ? isFreeTextMedication(editingMedication.value) : false;
});

const editingMedIsPrescribed = computed(() => {
    return editingMedication.value ? isPrescribedMedication(editingMedication.value) : false;
});

const medNameBlurred = ref(false);
const onMedNameBlur = () => {
    medNameBlurred.value = true;
};
const showFreeTextWarning = computed(() => {
    return !isPortal && effectiveScriptSurePatientId.value && medNameBlurred.value && editingMedication.value?.medication?.CodeableConcept?.text && editingMedIsFreeText.value;
});

// History state
const historyData = ref(null);
const historyLoading = ref(false);

const fetchMedicationHistory = async (medicationId) => {
    if (!medicationId) {
        historyData.value = null;
        return;
    }
    historyLoading.value = true;
    try {
        const response = await $httpFhirApi.get(`MedicationRequest/${medicationId}/_history`);
        historyData.value = response.data?.entry?.map((e) => e.resource) || (Array.isArray(response.data) ? response.data : []);
    } catch (err) {
        console.error('Failed to fetch medication history:', err);
        historyData.value = [];
    } finally {
        historyLoading.value = false;
    }
};

const getNestedValue = (obj, path) => {
    return path.split('.').reduce((o, key) => {
        if (!o) return undefined;
        const match = key.match(/^(.+)\[(\d+)\]$/);
        if (match) return o[match[1]]?.[parseInt(match[2])];
        return o[key];
    }, obj);
};

const describeHistoryChanges = (current, previous) => {
    const changes = [];
    const curJson = typeof current.toJSON === 'function' ? current.toJSON() : current;
    const prevJson = typeof previous.toJSON === 'function' ? previous.toJSON() : previous;

    const fields = [
        { path: 'medication.CodeableConcept.text', label: 'Medication name' },
        { path: 'status', label: 'Status' },
        { path: 'ncpdpStatus', label: 'Pharmacy status' },
        { path: 'effective.start', label: 'Start date', format: formatDate },
        { path: 'effective.end', label: 'End date', format: formatDate },
        { path: 'dosageInstruction[0].timing.code.text', label: 'Frequency' },
        { path: 'reasonCode[0].text', label: 'Reason' },
        { path: 'isMedicationEffective.display', label: 'Effectiveness' },
        { path: 'takenAsDirected.display', label: 'Taken as directed' },
        { path: 'includeInTreatmentPlan', label: 'Treatment plan', boolean: true },
    ];

    for (const field of fields) {
        const curVal = getNestedValue(curJson, field.path);
        const prevVal = getNestedValue(prevJson, field.path);
        if (String(curVal ?? '') !== String(prevVal ?? '')) {
            if (field.boolean) {
                changes.push(curVal ? `Added to ${field.label.toLowerCase()}` : `Removed from ${field.label.toLowerCase()}`);
            } else {
                const displayCur = field.format ? field.format(curVal) : curVal;
                const displayPrev = field.format ? field.format(prevVal) : prevVal;
                if (prevVal && curVal) {
                    changes.push(`${field.label}: ${displayPrev} → ${displayCur}`);
                } else if (curVal) {
                    changes.push(`${field.label}: ${displayCur}`);
                } else {
                    changes.push(`${field.label} removed`);
                }
            }
        }
    }

    return changes;
};

const isWebhookVersion = (entry, originalIndex) => {
    if (!historyData.value || originalIndex >= historyData.value.length - 1) return false;
    const prev = historyData.value[originalIndex + 1];
    const curLogLen = entry.scriptSureWebhookLog?.length ?? 0;
    const prevLogLen = prev.scriptSureWebhookLog?.length ?? 0;
    return curLogLen > prevLogLen;
};

const filteredHistoryData = computed(() => {
    if (!historyData.value) return null;
    if (historyData.value.length <= 1) {
        return historyData.value.map((entry, i) => ({ entry, originalIndex: i }));
    }
    const result = [];
    for (let i = 0; i < historyData.value.length; i++) {
        const isFirstVersion = i === historyData.value.length - 1;
        if (isFirstVersion || describeHistoryChanges(historyData.value[i], historyData.value[i + 1]).length) {
            result.push({ entry: historyData.value[i], originalIndex: i });
        }
    }
    return result;
});

// Get medication status color (same as card)
const getStatusColor = (status) => {
    switch (status) {
        case 'active':
            return 'success';
        case 'completed':
            return 'primary';
        case 'stopped':
        case 'cancelled':
            return 'error';
        case 'on-hold':
            return 'warning';
        case 'draft':
            return 'grey';
        default:
            return 'primary';
    }
};

const ncpdpStatusConfig = {
    Sent: { label: 'Sent to Pharmacy', color: 'blue' },
    Acknowledged: { label: 'Pharmacy Acknowledged', color: 'green' },
    Accepted: { label: 'Pharmacy Accepted', color: 'green' },
    Cancelled: { label: 'Cancelled', color: 'error' },
    'RxChange Response': { label: 'Change Requested', color: 'orange' },
    'RxRenewal Response': { label: 'Renewal Response', color: 'teal' },
};

const getNcpdpDisplay = (ncpdpStatus) => {
    if (!ncpdpStatus) return null;
    if (ncpdpStatusConfig[ncpdpStatus]) return ncpdpStatusConfig[ncpdpStatus];
    if (ncpdpStatus.startsWith('Error')) return { label: 'Pharmacy Error', color: 'error' };
    return { label: ncpdpStatus, color: 'grey' };
};
</script>

<template>
    <v-card variant="flat" :border="false" class="pb-2">
        <div v-if="!isCompact" class="d-flex py-1 text-h2" style="color: #43a047">
            <FontAwesomeIcon size="lg" :icon="['far', 'pills']" class="align-self-center mr-2"></FontAwesomeIcon>
            <v-list-item-title class="text-h2">{{ title }}</v-list-item-title>
        </div>
        <div v-if="!canViewMedications" :style="!isCompact ? 'border-left: 3px solid #43a047; opacity: 1; padding-left: 8px; color: black' : 'color: black'">
            <div :class="{ inactiveFontSize: isLocked || printView }">
                <div class="text-wrap text-body-1">Permission denied</div>
            </div>
        </div>

        <DataProvider v-else ref="medicationsDP" v-model="medicationRequestData" collection autosave refresh-on-change v-bind="medicationQuery">
            <template #loading>
                <v-skeleton-loader class="mx-auto" width="100%" type="list-item-three-line" />
            </template>
            <template #error="{ error, refresh }">
                <div>
                    {{ error }}
                    <v-btn @click="refresh">Try Again</v-btn>
                </div>
            </template>
            <template #default>
                <div data-cy="currentMedicationList" data-linkid="medicationList">
                    <div v-if="!isCompact" class="text-medium-emphasis mb-2">Include current and any relevant past medications.</div>

                    <!-- Read-only medications list -->
                    <div :style="!isCompact ? 'border-left: 3px solid #43a047; opacity: 1; padding-left: 8px' : ''">
                        <template v-if="displayedMedications.length">
                            <!-- Reconcile mode toolbar -->
                            <div v-if="reconcileMode" class="d-flex align-center px-2 pt-2" style="height: 36px" data-cy="reconcileToolbar">
                                <v-btn size="x-small" variant="text" color="primary" class="mr-2" data-cy="toggleAllReconcile" @click="toggleAllForReconcile">
                                    {{ allSelectedForReconcile ? 'Deselect all' : 'Select all' }}
                                </v-btn>
                                <v-btn
                                    size="x-small"
                                    color="primary"
                                    variant="tonal"
                                    :disabled="selectedCount === 0"
                                    :loading="reconciling"
                                    data-cy="reconcileSelectedBtn"
                                    @click="reconcileMedications"
                                >
                                    <FontAwesomeIcon :icon="['far', 'check-double']" class="mr-2" />
                                    Reconcile selected ({{ selectedCount }})
                                </v-btn>
                                <v-spacer v-if="isCompact" />
                                <v-btn size="x-small" variant="text" color="secondary" data-cy="cancelReconcile" :class="{ 'ml-4': !isCompact }" icon @click="toggleReconcileMode">
                                    <FontAwesomeIcon :icon="['far', 'times']" />
                                </v-btn>
                            </div>
                            <div v-if="isCompact && canReconcile && !reconcileMode && !isPortal" class="d-flex align-center px-2 pt-2" style="height: 36px">
                                <v-btn color="primary" variant="tonal" size="x-small" class="flex-grow-1" data-cy="reconcileMedicationsBtn" @click="toggleReconcileMode">
                                    <FontAwesomeIcon :icon="['far', 'check-double']" class="mr-2" />
                                    Reconcile medications
                                </v-btn>
                                <BnMenu icon="ellipsis-v" btn-size="x-small" data-cy="medicationsOverflowMenu">
                                    <BnMenuItem
                                        :icon="isRefreshing ? 'spinner' : 'rotate'"
                                        label="Refresh medications"
                                        :disabled="isRefreshing"
                                        data-cy="refreshMedicationsMenuItem"
                                        @click="refreshMedications"
                                    />
                                </BnMenu>
                            </div>

                            <v-list lines="two" class="pa-0 bg-transparent">
                                <template v-for="(med, index) in displayedMedications" :key="med.id || index">
                                    <v-divider v-if="index"></v-divider>
                                    <v-list-item
                                        :data-cy="'medicationItem-' + index"
                                        :class="{ 'cursor-pointer': !reconcileMode && !viewOnlyMedications && !readOnly }"
                                        @click="reconcileMode ? toggleMedicationSelection(med.id) : openEditDialog(med)"
                                    >
                                        <template #prepend>
                                            <div class="reconcile-checkbox-wrapper" :class="{ 'reconcile-checkbox-active': reconcileMode }">
                                                <v-checkbox-btn
                                                    v-if="reconcileMode"
                                                    :model-value="selectedForReconcile.has(med.id)"
                                                    :data-cy="'reconcileCheckbox-' + index"
                                                    @update:model-value="toggleMedicationSelection(med.id)"
                                                    @click.stop
                                                />
                                            </div>
                                        </template>

                                        <template #title>
                                            <v-tooltip open-delay="500" location="right">
                                                <template #activator="{ props: tooltipProps }">
                                                    <div v-bind="tooltipProps" class="d-flex align-center" style="min-width: 0">
                                                        <span class="text-truncate font-weight-bold">{{ med.medication?.CodeableConcept?.text }}</span>
                                                    </div>
                                                </template>
                                                <div class="px-2">{{ med.medication?.CodeableConcept?.text }}</div>
                                                <div v-if="med.dosageInstruction?.[0]?.timing?.code?.text" class="px-2">Frequency: {{ med.dosageInstruction[0].timing.code.text }}</div>
                                            </v-tooltip>
                                        </template>

                                        <div class="d-flex flex-wrap align-center gap-1 mt-1">
                                            <template v-if="!isPortal">
                                                <v-chip v-if="isMedicationInactive(med)" size="small" label color="grey"> <b>Inactive</b> </v-chip>
                                                <v-chip v-else-if="med.status" size="small" label :color="getStatusColor(med.status)">
                                                    <b>{{ med.status.charAt(0).toUpperCase() + med.status.slice(1) }}</b>
                                                </v-chip>
                                                <v-chip
                                                    v-if="isMedicationInactive(med) && med.statusReason?.text"
                                                    size="small"
                                                    label
                                                    variant="flat"
                                                    color="yellow-lighten-3"
                                                    class="ml-1 text-truncate"
                                                    style="max-width: 300px"
                                                    data-cy="inactiveReasonChip"
                                                >
                                                    Reason: {{ med.statusReason.text }}
                                                </v-chip>
                                            </template>
                                            <v-chip v-if="isPrescribedMedication(med)" size="small" label color="info" variant="flat" class="mx-1" data-cy="prescribedChip">
                                                <b>Prescribed</b>
                                            </v-chip>
                                            <!-- Treatment plan indicator — hidden in treatment-plan-only contexts and in the portal -->
                                            <v-chip
                                                v-if="med.includeInTreatmentPlan && !treatmentPlanMedsOnly && !isPortal"
                                                size="small"
                                                label
                                                variant="outlined"
                                                color="teal"
                                                class="ml-2"
                                                data-cy="treatmentPlanChip"
                                            >
                                                Tx plan
                                            </v-chip>
                                            <v-tooltip
                                                v-if="!isPortal && effectiveScriptSurePatientId && isFreeTextMedication(med) && !isMedicationInactive(med) && !isPrescribedMedication(med)"
                                                location="top"
                                            >
                                                <template #activator="{ props: tipProps }">
                                                    <v-chip v-bind="tipProps" size="small" label color="warning" variant="flat" class="ml-1" data-cy="freeTextSyncChip">
                                                        <FontAwesomeIcon :icon="['far', 'exclamation-triangle']" class="mr-1" />
                                                        <b>Non e-Prescribe medication</b>
                                                    </v-chip>
                                                </template>
                                                <span>Free-text medication — select from search results to enable e-Prescribe sync</span>
                                            </v-tooltip>
                                            <span v-if="med.effective?.start" class="text-body-1" :class="{ 'ml-2': !isPortal }">
                                                {{ formatDate(med.effective.start) }} - {{ med.effective?.end ? formatDate(med.effective.end) : 'Current' }}
                                            </span>
                                            <span v-if="med.dosageInstruction?.[0]?.timing?.code?.text" class="text-body-1 ml-1"> &bull; {{ med.dosageInstruction[0].timing.code.text }} </span>
                                            <span v-if="med.reasonCode?.[0]?.text" class="text-body-1 ml-1"> &bull; Reason: {{ med.reasonCode[0].text }} </span>
                                        </div>
                                        <div v-if="getLastReconciliation(med)" class="mt-1">
                                            <span class="text-caption text-grey" style="font-size: 0.7rem" data-cy="reconcileInfo">
                                                <FontAwesomeIcon :icon="['far', 'check-double']" class="mr-1" />
                                                Reconciled {{ formatDate(getLastReconciliation(med).date) }} &mdash; {{ getDisplay(getLastReconciliation(med).practitioner) }}
                                            </span>
                                        </div>

                                        <div v-if="getLatestWebhookLog(med)" class="mt-1">
                                            <span class="text-caption text-grey">
                                                {{ getLatestWebhookLog(med).event }}
                                                <template v-if="getLatestWebhookLog(med).prescriber"> by {{ getLatestWebhookLog(med).prescriber }}</template>
                                                <template v-if="getLatestWebhookLog(med).pharmacy"> to {{ getLatestWebhookLog(med).pharmacy }}</template>
                                                <template v-if="getLatestWebhookLog(med).receivedAt"> &mdash; {{ formatDate(getLatestWebhookLog(med).receivedAt) }}</template>
                                            </span>
                                        </div>
                                    </v-list-item>
                                </template>
                            </v-list>
                        </template>

                        <!-- Treatment plan only - no medications flagged -->
                        <template v-else-if="treatmentPlanMedsOnly">
                            <v-list class="pa-0 bg-transparent">
                                <v-list-item class="d-flex justify-center align-center" data-cy="noTreatmentPlanMeds" title="No medications included in the treatment plan." />
                            </v-list>
                        </template>

                        <!-- Empty state for compact mode - hint to add -->
                        <template v-else-if="isCompact">
                            <v-card :border="false" flat class="d-flex text-caption pa-2 flex-grow-1">
                                <div class="ml-auto">
                                    Add medication
                                    <FontAwesomeIcon :icon="['far', 'level-up']" class="ml-2 mr-16" />
                                </div>
                            </v-card>
                        </template>
                    </div>

                    <!-- Action buttons -->
                    <div v-if="!isCompact && !viewOnlyMedications && !readOnly" class="d-flex flex-wrap gap-2 mt-2">
                        <v-btn color="primary" variant="tonal" size="x-small" class="v-list-item-pointer-hover" data-cy="addAnotherMedicationBtn" @click="openAddDialog">
                            <template #prepend>
                                <FontAwesomeIcon :icon="['far', 'plus']" size="sm" />
                            </template>
                            Add medication
                        </v-btn>
                        <v-btn
                            v-if="canReconcile && !reconcileMode && !isPortal"
                            color="primary"
                            variant="tonal"
                            size="x-small"
                            class="v-list-item-pointer-hover ml-2"
                            data-cy="reconcileMedicationsBtn"
                            @click="toggleReconcileMode"
                        >
                            <template #prepend>
                                <FontAwesomeIcon :icon="['far', 'check-double']" size="sm" />
                            </template>
                            Reconcile medications
                        </v-btn>
                        <v-btn
                            v-if="canPrescribe"
                            color="primary"
                            size="x-small"
                            class="v-list-item-pointer-hover ml-2"
                            :loading="isPrescribing"
                            :disabled="isPrescribing"
                            data-cy="ePrescribeBtn"
                            @click="openPrescribe"
                        >
                            e-Prescribe
                            <FontAwesomeIcon :icon="['far', 'arrow-up-right-from-square']" class="ml-2" />
                        </v-btn>
                        <BnMenu icon="ellipsis-v" btn-size="x-small" data-cy="medicationsOverflowMenu">
                            <BnMenuItem
                                :icon="isRefreshing ? 'spinner' : 'rotate'"
                                label="Refresh medications"
                                :disabled="isRefreshing"
                                data-cy="refreshMedicationsMenuItem"
                                @click="refreshMedications"
                            />
                        </BnMenu>
                    </div>
                </div>
            </template>
        </DataProvider>

        <!-- Confirmation Dialog -->
        <BnDialog ref="bnDialogRef" />

        <!-- Add/Edit Medication Dialog -->
        <v-dialog v-model="editDialog" scrollable persistent :fullscreen="vuetifyDisplay.xs.value" max-width="600px">
            <v-card v-if="editingMedication" class="d-flex flex-column">
                <ion-header>
                    <ion-toolbar>
                        <v-toolbar density="compact" flat class="flex-grow-0">
                            <v-toolbar-title>{{ isNewMedication ? 'Add' : 'Edit' }} medication</v-toolbar-title>
                            <BnToolbarBtn type="text" label="Cancel" icon="times" color="secondary" data-cy="cancelMedicationEdit" @click="cancelEdit" />
                            <BnToolbarBtn
                                type="outlined"
                                right
                                label="Save"
                                icon="check"
                                color="primary"
                                :disabled="!!v$.medicationForm.$errors.length"
                                data-cy="saveMedication"
                                @click="saveMedication"
                            />
                            <BnMenu v-if="!isNewMedication" offset-y left icon="ellipsis-v" data-cy="medicationEditMenu">
                                <BnMenuItem label="Save" icon="check" :disabled="!!v$.medicationForm.$errors.length" @click="saveMedication" />
                                <BnMenuItem label="Cancel" icon="times" @click="cancelEdit" />
                                <BnMenuItem
                                    v-if="!treatmentPlanMedsOnly && !isPortal"
                                    :icon="editingMedication.includeInTreatmentPlan ? 'square-check' : 'square'"
                                    label="Include in treatment plan"
                                    data-cy="toggleTreatmentPlanBtn"
                                    @click="toggleTreatmentPlanFromDialog"
                                />
                                <BnMenuItem
                                    v-if="isEditingMedicationInactive"
                                    icon="inbox-out"
                                    label="Restore medication"
                                    data-cy="restoreMedicationBtn"
                                    @click="restoreMedication(editingMedication)"
                                />
                                <template v-else>
                                    <BnMenuItem v-if="!isPortal" icon="archive" label="Discontinue medication" data-cy="inactivateMedicationBtn" @click="inactivateMedication(editingMedication)" />
                                    <BnMenuItem icon="exclamation-triangle" label="Entered in error" data-cy="enteredInErrorBtn" @click="markEnteredInError(editingMedication)" />
                                </template>
                            </BnMenu>
                        </v-toolbar>
                    </ion-toolbar>
                </ion-header>
                <v-divider></v-divider>
                <v-card-text class="pa-4 flex-grow-1 overflow-auto">
                    <v-alert v-if="editingMedIsPrescribed" type="info" variant="tonal" density="compact" class="mb-4" data-cy="prescribedMedicationAlert">
                        This medication was prescribed via e-Prescribe. Prescription details are locked, but clinical assessments can be updated.
                        <span v-if="editingMedication.ncpdpStatus" class="font-weight-bold ml-1">Status: {{ getNcpdpDisplay(editingMedication.ncpdpStatus).label }}</span>
                    </v-alert>
                    <div class="d-flex flex-column">
                        <!-- Medication name -->
                        <v-combobox
                            v-model:search="searchRxNorm"
                            :model-value="editingMedication.medication?.CodeableConcept?.text"
                            :items="medList"
                            :data-options="JSON.stringify(medList)"
                            :menu-props="{ closeOnContentClick: true }"
                            :disabled="editingMedIsPrescribed"
                            item-title="display"
                            return-object
                            label="Medication name"
                            data-cy="medicationField"
                            v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'medicationName', true)"
                            @update:model-value="(item) => setMedication(editingMedication, item)"
                            @blur="onMedNameBlur"
                        >
                            <template v-if="showFreeTextWarning" #append>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props: tipProps }">
                                        <FontAwesomeIcon v-bind="tipProps" :icon="['far', 'exclamation-triangle']" class="text-warning" data-cy="freeTextMedicationWarning" />
                                    </template>
                                    <span>This medication was entered manually and won't sync to e-Prescribe. Select a medication from the search results to enable syncing.</span>
                                </v-tooltip>
                            </template>
                            <template #item="{ item, props: itemProps }">
                                <v-list-item v-bind="itemProps" :title="null">
                                    <v-list-item-title data-cy="medicationSearchItem">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </template>
                        </v-combobox>

                        <!-- Dates row -->
                        <div class="d-flex flex-wrap">
                            <div style="width: 150px" class="mr-2">
                                <BnDatePicker
                                    :model-value="editingMedication.effective?.start"
                                    label="Start date"
                                    data-cy="dateOfFirstUseField"
                                    :disabled="editingMedIsPrescribed || !editingMedication.medication?.CodeableConcept?.text"
                                    :strict-date="false"
                                    @update:model-value="(val) => updateEffective(editingMedication, 'start', val)"
                                ></BnDatePicker>
                            </div>
                            <div style="width: 150px">
                                <BnDatePicker
                                    :model-value="editingMedication.effective?.end"
                                    label="End date"
                                    data-cy="dateOfLastUseField"
                                    :disabled="editingMedIsPrescribed || !editingMedication.medication?.CodeableConcept?.text"
                                    :strict-date="false"
                                    @update:model-value="(val) => updateEffective(editingMedication, 'end', val)"
                                ></BnDatePicker>
                            </div>
                        </div>

                        <!-- Inactive reason — appears only when End date has a value -->
                        <v-text-field
                            v-if="editingMedication.effective?.end"
                            :model-value="editingMedication.statusReason?.text"
                            :disabled="editingMedIsPrescribed"
                            label="Inactive reason"
                            data-cy="inactiveReasonField"
                            @update:model-value="(val) => (editingMedication.statusReason = val ? { text: val } : undefined)"
                        ></v-text-field>

                        <!-- Frequency and taken as directed row -->
                        <div class="d-flex flex-wrap">
                            <v-text-field
                                :model-value="editingMedication.dosageInstruction?.[0]?.timing?.code?.text || editingMedication.dosageInstruction?.[0]?.text"
                                :disabled="editingMedIsPrescribed || !editingMedication.medication?.CodeableConcept?.text"
                                class="flex-grow-1 mr-2"
                                style="min-width: 150px"
                                :label="editingMedIsPrescribed ? 'Sig' : 'Frequency'"
                                data-cy="frequencyField"
                                @update:model-value="(val) => updateFrequency(editingMedication, val)"
                            ></v-text-field>

                            <v-autocomplete
                                v-model="editingMedication.takenAsDirected"
                                :disabled="!editingMedication.medication?.CodeableConcept?.text"
                                :items="takenAsDirectedOptions"
                                :data-options="JSON.stringify(takenAsDirectedOptions)"
                                item-title="display"
                                item-value="code"
                                return-object
                                label="Taken as directed?"
                                class="flex-grow-1"
                                style="min-width: 150px"
                                clearable
                                data-cy="currentMedsTakenAsDirectedSelect"
                                @click:clear="editingMedication.takenAsDirected = undefined"
                            >
                                <template #no-data>
                                    <v-list-item>
                                        <v-list-item-title> No option found </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Effectiveness -->
                        <v-autocomplete
                            v-model="editingMedication.isMedicationEffective"
                            :disabled="!editingMedication.medication?.CodeableConcept?.text"
                            :items="effectivenessOptions"
                            :data-options="JSON.stringify(effectivenessOptions)"
                            item-title="display"
                            item-value="code"
                            return-object
                            label="Effectiveness"
                            clearable
                            data-cy="currentMedsIsEffectiveSelect"
                            @click:clear="editingMedication.isMedicationEffective = undefined"
                        >
                            <template #no-data>
                                <v-list-item>
                                    <v-list-item-title> No option found </v-list-item-title>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Reason for taking -->
                        <v-text-field
                            :model-value="editingMedication.reasonCode?.[0]?.text"
                            :disabled="!editingMedication.medication?.CodeableConcept?.text"
                            label="Reason for taking medication"
                            data-cy="symptomField"
                            @update:model-value="(val) => (editingMedication.reasonCode = [{ text: val }])"
                        ></v-text-field>
                        <!-- "Include in treatment plan" checkbox — also toggleable via the ellipsis menu item above -->
                        <v-checkbox
                            v-if="!treatmentPlanMedsOnly && !isPortal"
                            v-model="editingMedication.includeInTreatmentPlan"
                            :disabled="!editingMedication.medication?.CodeableConcept?.text"
                            label="Include in treatment plan"
                            density="comfortable"
                            hide-details
                            data-cy="includeInTreatmentPlanCheckbox"
                        />
                    </div>

                    <!-- History -->
                    <template v-if="!isNewMedication">
                        <BnListBox title="History" data-cy="medicationHistory">
                            <v-card v-if="historyLoading" flat border="none" class="d-flex text-caption mb-2 pt-2 pr-2 flex-grow-1">
                                <div class="ml-2">
                                    <FontAwesomeIcon :icon="['far', 'spinner']" class="fa-spin mr-1" />
                                    Loading...
                                </div>
                            </v-card>
                            <v-card v-else-if="!filteredHistoryData || !filteredHistoryData.length" flat border="none" class="d-flex text-caption mb-2 pt-2 pr-2 flex-grow-1">
                                <div class="ml-2">No history found</div>
                            </v-card>
                            <v-card v-else flat border="none" style="max-height: 300px; overflow-y: auto">
                                <v-list>
                                    <template v-for="(h, index) in filteredHistoryData" :key="index">
                                        <v-divider v-if="index" class="my-2" />
                                        <v-row class="v-row--no-gutters">
                                            <v-col cols="7" class="align-self-center pl-4">
                                                <v-chip size="x-small" label :color="getStatusColor(h.entry.status)">
                                                    {{ h.entry.status?.charAt(0).toUpperCase() + h.entry.status?.slice(1) }}
                                                </v-chip>
                                                <span v-if="h.originalIndex === historyData.length - 1" class="pl-1">
                                                    <v-chip size="x-small" label color="success">Record created</v-chip>
                                                </span>
                                                <div v-else class="mt-1">
                                                    <div
                                                        v-for="(change, cIdx) in describeHistoryChanges(h.entry, historyData[h.originalIndex + 1])"
                                                        :key="cIdx"
                                                        class="text-caption text-medium-emphasis"
                                                    >
                                                        {{ change }}
                                                    </div>
                                                </div>
                                            </v-col>
                                            <v-col cols="5" class="text-caption text-right pr-4">
                                                <span :class="{ 'text-grey': h.originalIndex === historyData.length - 1 }">
                                                    Updated by {{ isWebhookVersion(h.entry, h.originalIndex) ? 'e-Prescribe' : getDisplay(h.entry.updatedBy) || '—' }}
                                                    <br />
                                                    {{ formatDate(h.entry.meta?.lastUpdated) }}
                                                </span>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </v-list>
                            </v-card>
                        </BnListBox>
                    </template>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Discontinue Medication Dialog -->
        <v-dialog v-model="discontinueDialog" persistent max-width="500px" data-cy="discontinueDialog">
            <v-card>
                <v-toolbar density="compact" flat>
                    <v-toolbar-title>Discontinue medication</v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <div style="max-width: 200px">
                        <BnDatePicker
                            :model-value="discontinueDate"
                            label="Discontinued date"
                            data-cy="discontinueDateField"
                            :strict-date="false"
                            @update:model-value="(val) => (discontinueDate = val)"
                        ></BnDatePicker>
                    </div>
                    <v-textarea v-model="discontinueReason" label="Inactive reason" data-cy="discontinueReasonField" rows="3" auto-grow hide-details="auto"></v-textarea>
                </v-card-text>
                <v-card-actions class="pa-4 pt-0">
                    <v-spacer></v-spacer>
                    <v-btn variant="text" color="secondary" data-cy="cancelDiscontinueBtn" @click="cancelDiscontinue">Cancel</v-btn>
                    <v-btn variant="outlined" color="primary" data-cy="confirmDiscontinueBtn" @click="confirmDiscontinue">Discontinue</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ScriptSure missing data notification -->
        <v-snackbar v-model="scriptSureSnackbar" :timeout="6000" color="warning" location="bottom" multi-line>
            {{ scriptSureSnackbarMessage }}
            <template #actions>
                <v-btn variant="outlined" color="white" @click="scriptSureSnackbar = false">Close</v-btn>
            </template>
        </v-snackbar>
    </v-card>
</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.reconcile-checkbox-wrapper {
    width: 0;
    opacity: 0;
    overflow: hidden;
    transition:
        width 0.2s ease,
        opacity 0.2s ease;
}

.reconcile-checkbox-active {
    width: 40px;
    opacity: 1;
}
</style>
